import React, { useState } from "react";

/**
 * QueryPreviewCard
 * ─────────────────
 * Shows the generated SQL/SOQL before execution and provides
 * [Run Query] and [Cancel] buttons.
 *
 * Props
 * -----
 * data         : structured_data payload with structured_data_type === "query_preview"
 * onApprove    : () => void  — called when user clicks [Run Query]
 * onCancel     : () => void  — called when user clicks [Cancel]
 * disabled     : bool        — true when not the latest message
 */
const QueryPreviewCard = ({ data, onApprove, onCancel, disabled }) => {
  const [isQueryExpanded, setIsQueryExpanded] = useState(false);

  if (!data) return null;

  const { platform = "", query = "", description = "", all_pending = [] } = data;
  const pendingCount = all_pending.length;

  const platformColors = {
    SNOWFLAKE: "bg-blue-50 text-blue-700 border-blue-200",
    SALESFORCE: "bg-indigo-50 text-indigo-700 border-indigo-200",
    HUBSPOT: "bg-orange-50 text-orange-700 border-orange-200",
    POSTGRESQL: "bg-teal-50 text-teal-700 border-teal-200",
    MYSQL: "bg-teal-50 text-teal-700 border-teal-200",
    AWS_S3: "bg-amber-50 text-amber-700 border-amber-200",
    "REST API": "bg-purple-50 text-purple-700 border-purple-200",
  };
  const platformColor = platformColors[platform?.toUpperCase()] || "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <div className="w-full max-w-2xl rounded-2xl border border-blue-200 bg-white shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-[0.85rem] font-bold text-blue-700">Query Preview</span>
          {pendingCount > 1 && (
            <span className="text-[0.7rem] font-bold text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">
              {pendingCount} queries
            </span>
          )}
        </div>
        <span className={`text-[0.7rem] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${platformColor}`}>
          {platform}
        </span>
      </div>

      {/* Description */}
      {description && (
        <div className="px-4 pt-3 pb-1 text-[0.88rem] text-gray-700 font-medium leading-relaxed">
          {description}
        </div>
      )}

      {/* Query block */}
      <div className="px-4 pt-2 pb-3">
        <button
          onClick={() => setIsQueryExpanded(v => !v)}
          className="flex items-center gap-1.5 text-[0.72rem] font-semibold text-gray-400 hover:text-gray-600 transition-colors mb-1.5"
        >
          <svg
            className={`w-3 h-3 transition-transform duration-200 ${isQueryExpanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
          {isQueryExpanded ? "Hide query" : "Show query"}
        </button>
        <div className={`overflow-hidden transition-all duration-300 ${isQueryExpanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"}`}>
          <pre className="bg-gray-900 text-green-300 text-[0.78rem] p-4 rounded-xl overflow-x-auto leading-relaxed font-mono whitespace-pre-wrap">
            {query}
          </pre>
        </div>
      </div>

      {/* Multiple queries badge */}
      {pendingCount > 1 && (
        <div className="px-4 pb-2">
          <p className="text-[0.75rem] text-gray-500 italic">
            +{pendingCount - 1} more {pendingCount - 1 === 1 ? "query" : "queries"} will also run.
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="px-4 pb-4 flex items-center gap-3">
        <button
          onClick={onApprove}
          disabled={disabled}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[0.85rem] font-bold transition-all shadow-sm
            ${disabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md active:scale-95"
            }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
          </svg>
          Run Query
        </button>
        <button
          onClick={onCancel}
          disabled={disabled}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[0.85rem] font-bold transition-all
            ${disabled
              ? "border border-gray-200 text-gray-300 cursor-not-allowed"
              : "border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-500 hover:bg-red-50 active:scale-95"
            }`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default QueryPreviewCard;
